import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, NativeSelect, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Paperclip, Send, Check, CheckCheck, ArrowLeft, FileText, StickyNote, X } from "lucide-react";

const STATUSES = ["Open", "Pending Response", "In Progress", "Resolved", "Closed"];
const TEAMS = ["Help Desk", "Accounting", "Treasury"];
const VIEWS = [["all", "All"], ["unassigned", "Unassigned"], ["mine", "Mine"], ["unread", "Unread"]];
const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";

// Polls fn every 7 s while the tab is visible; doubles the delay (max 60 s) after failures.
function usePoll(fn, deps) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    let delay = 7000, timer, alive = true;
    const tick = async () => {
      if (!document.hidden) {
        try { await ref.current(); delay = 7000; } catch { delay = Math.min(delay * 2, 60000); }
      }
      if (alive) timer = setTimeout(tick, delay);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

function Counters({ stats }) {
  const items = [["open", "Open"], ["unread", "Unread"], ["unassigned", "Unassigned"], ["awaiting_reply", "Awaiting reply"], ["resolved", "Resolved"]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="inbox-counters">
      {items.map(([k, l]) => (
        <div key={k} className="bg-white border border-slate-200 rounded-lg px-4 py-3 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 truncate">{l}</p>
          <p className="font-display text-2xl font-bold" data-testid={`inbox-count-${k}`}>{stats?.[k] ?? "—"}</p>
        </div>
      ))}
    </div>
  );
}

function ConversationRow({ c, active, onOpen }) {
  return (
    <button onClick={() => onOpen(c.id)} data-testid={`conversation-row-${c.id}`}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors hover:bg-slate-50 ${active ? "bg-blue-50" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <p className={`text-sm truncate ${c.staff_unread ? "font-semibold" : "font-medium"}`}>{c.subject}</p>
        {c.staff_unread > 0 && <span className="shrink-0 rounded-full bg-blue-700 text-white text-[10px] px-2 py-0.5" data-testid={`unread-badge-${c.id}`}>{c.staff_unread}</span>}
      </div>
      <p className="text-xs text-slate-600 truncate">{c.consumer_name} · <span data-testid={`account-type-${c.id}`}>{c.account_type_label}</span>{c.organization_name ? ` · ${c.organization_name}` : ""}</p>
      <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_sender_role === "staff" ? "Staff: " : ""}{c.last_message_preview}</p>
      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px] text-slate-500">
        <StatusBadge status={c.status} /><span className="font-mono">{c.reference}</span><span>{c.assigned_team}</span>
        <span>{c.assigned_agent_name || "Unassigned"}</span><span className="ml-auto">{fmtDate(c.last_message_at)}</span>
      </div>
    </button>
  );
}

async function openAttachment(a) {
  try {
    const r = await api.get(`/staff/chat/attachments/${a.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank", "noopener");
  } catch (e) { toast.error(errMsg(e)); }
}

function Receipt({ m }) {
  if (m.receipt === "read") return <span className={`inline-flex items-center gap-0.5 font-semibold ${m.sender_role === "staff" ? "text-yellow-300" : "text-blue-600"}`} data-testid={`receipt-${m.id}`}><CheckCheck className="h-3 w-3" />Read</span>;
  if (m.receipt === "delivered") return <span className="inline-flex items-center gap-0.5" data-testid={`receipt-${m.id}`}><CheckCheck className="h-3 w-3" />Delivered</span>;
  return <span className="inline-flex items-center gap-0.5" data-testid={`receipt-${m.id}`}><Check className="h-3 w-3" />Sent</span>;
}

function Message({ m }) {
  const staff = m.sender_role === "staff";
  return (
    <div className={`flex ${staff ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
      <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 ${staff ? "bg-blue-700 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
        <p className={`text-[11px] mb-0.5 ${staff ? "text-blue-100" : "text-slate-500"}`}>{m.sender_name}</p>
        {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
        {m.attachments.map((a) => (
          <button key={a.id} onClick={() => openAttachment(a)} data-testid={`attachment-${a.id}`}
            className={`mt-1.5 flex items-center gap-1.5 text-xs underline ${staff ? "text-white" : "text-blue-700"}`}>
            <FileText className="h-3.5 w-3.5" />{a.original_name} ({Math.ceil(a.size / 1024)} KB)
          </button>
        ))}
        <p className={`text-[10px] mt-1 flex gap-2 justify-end ${staff ? "text-blue-100" : "text-slate-400"}`}>{fmtDate(m.created_at)}<Receipt m={m} /></p>
      </div>
    </div>
  );
}

function ReplyBox({ conv, onSent }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const closed = conv.status === "Closed";
  const pick = (list) => {
    const ok = [...list].filter((f) => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is larger than 10 MB`); return false; }
      return true;
    });
    setFiles((p) => [...p, ...ok].slice(0, 5));
  };
  const send = async () => {
    if (!text.trim() && !files.length) return;
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        ids.push((await api.post(`/staff/chat/conversations/${conv.id}/attachments`, fd)).data.id);
      }
      await api.post(`/staff/chat/conversations/${conv.id}/messages`, { text, attachment_ids: ids, client_id: crypto.randomUUID() });
      setText(""); setFiles([]); onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (closed) return <p className="text-sm text-slate-500 p-4 border-t" data-testid="reply-closed">This conversation is closed. Reopen it to reply.</p>;
  return (
    <div className="border-t border-slate-200 p-3 bg-white">
      {files.length > 0 && <div className="flex flex-wrap gap-2 mb-2">{files.map((f, i) => (
        <span key={i} className="text-xs bg-slate-100 rounded px-2 py-1 flex items-center gap-1">{f.name}
          <X className="h-3 w-3 cursor-pointer" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`remove-file-${i}`} /></span>))}</div>}
      <div className="flex gap-2 items-end">
        <label className="cursor-pointer p-2 rounded-md hover:bg-slate-100" title="Attach JPG, PNG, WEBP or PDF (max 10 MB)">
          <Paperclip className="h-4 w-4" />
          <input type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid="reply-file-input" />
        </label>
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply to the consumer…" className="min-h-[44px]"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }} data-testid="reply-input" />
        <Button onClick={send} disabled={busy} data-testid="reply-send-button"><Send className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Send"}</Button>
      </div>
    </div>
  );
}

function SidePanel({ data, agents, reload }) {
  const c = data.conversation;
  const [note, setNote] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const setStatus = (s) => (s === "Closed" ? setPendingStatus(s) : act(() => api.post(`/staff/chat/conversations/${c.id}/status`, { status: s }), `Status set to ${s}`));
  return (
    <div className="space-y-4 min-w-0">
      <Panel title="Conversation" testId="conversation-details">
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500 break-words">{c.consumer_email}{c.account_number ? ` · Acct ${c.account_number}` : ""}</p>
          <div className="space-y-1"><p className="text-xs text-slate-500">Status</p>
            <NativeSelect value={c.status} onChange={setStatus} testId="status-select" options={STATUSES.map((s) => ({ value: s, label: s }))} className="w-full" /></div>
          <div className="space-y-1"><p className="text-xs text-slate-500">Team</p>
            <NativeSelect value={c.assigned_team || "Help Desk"} testId="team-select" options={TEAMS.map((s) => ({ value: s, label: s }))} className="w-full"
              onChange={(t) => { const a = agents.find((x) => x.id === c.assigned_agent_id); const keep = a && (a.team === t || a.all_teams);
                act(() => api.post(`/staff/chat/conversations/${c.id}/assign`, { agent_id: keep ? c.assigned_agent_id : null, team: t }), `Moved to ${t}`); }} /></div>
          <div className="space-y-1"><p className="text-xs text-slate-500">Assigned agent</p>
            <NativeSelect value={c.assigned_agent_id || ""} placeholder="Unassigned" testId="agent-select" className="w-full"
              options={agents.map((a) => ({ value: a.id, label: `${a.name || a.email} (${a.team})` }))}
              onChange={(id) => act(() => api.post(`/staff/chat/conversations/${c.id}/assign`, { agent_id: id || null, team: c.assigned_team }), "Assignment updated")} /></div>
        </div>
      </Panel>
      <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-4" data-testid="internal-notes-panel">
        <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-900"><StickyNote className="h-4 w-4" />Internal notes</p>
        <p className="text-[11px] text-amber-800 mb-3">Visible to staff only. Never shown to the consumer.</p>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for colleagues…" className="bg-white" data-testid="note-input" />
        <Button size="sm" variant="outline" className="mt-2" disabled={!note.trim()} data-testid="note-add-button"
          onClick={() => act(async () => { await api.post(`/staff/chat/conversations/${c.id}/notes`, { text: note }); setNote(""); }, "Note added")}>Add note</Button>
        <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">{data.internal_notes.map((n) => (
          <div key={n.id} className="bg-white/70 rounded p-2 text-xs" data-testid={`note-${n.id}`}><p className="whitespace-pre-wrap break-words">{n.text}</p>
            <p className="text-[10px] text-slate-500 mt-1">{n.author_name} · {fmtDate(n.created_at)}</p></div>))}</div>
      </div>
      <AlertDialog open={!!pendingStatus} onOpenChange={(o) => !o && setPendingStatus(null)}>
        <AlertDialogContent data-testid="close-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Close this conversation?</AlertDialogTitle>
            <AlertDialogDescription>The consumer will not be able to reply or reopen it. They can start a new conversation instead.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="close-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="close-confirm" onClick={() => { setPendingStatus(null); act(() => api.post(`/staff/chat/conversations/${c.id}/status`, { status: "Closed" }), "Conversation closed"); }}>Close conversation</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConversationView({ id, agents, onBack, onChanged }) {
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const bottom = useRef(null);
  const lastCount = useRef(0);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/staff/chat/conversations/${id}`);
      if (r.data.conversation.staff_unread > 0 || r.data.messages.some((m) => m.sender_role === "consumer" && !m.read_at)) {
        await api.post(`/staff/chat/conversations/${id}/read`);
        changed.current();
      }
      setData(r.data); setError(null);
    } catch (e) { setError(errMsg(e)); throw e; }
  }, [id]);
  usePoll(load, [id]);
  useEffect(() => {
    if (data && data.messages.length !== lastCount.current) { lastCount.current = data.messages.length; bottom.current?.scrollIntoView({ block: "end" }); }
  }, [data]);
  const reload = () => { load().catch(() => {}); onChanged(); };
  if (error && !data) return <Panel><p className="text-sm text-rose-700" data-testid="conversation-error">{error}</p></Panel>;
  if (!data) return <Panel><p className="text-sm text-slate-500">Loading conversation…</p></Panel>;
  const c = data.conversation;
  return (
    <div className="grid xl:grid-cols-[1fr_280px] gap-4 min-w-0" data-testid="conversation-view">
      <div className="bg-slate-50 border border-slate-200 rounded-lg flex flex-col min-w-0 h-[70vh]">
        <div className="px-4 py-3 border-b border-slate-200 bg-white rounded-t-lg flex items-center gap-3 min-w-0">
          <button className="lg:hidden" onClick={onBack} data-testid="conversation-back"><ArrowLeft className="h-4 w-4" /></button>
          <div className="min-w-0"><p className="font-semibold truncate" data-testid="conversation-subject">{c.subject}</p>
            <p className="text-xs text-slate-500 truncate">{c.consumer_name} · {c.account_type_label} · <span className="font-mono">{c.reference}</span></p></div>
          <span className="ml-auto"><StatusBadge status={c.status} testId="conversation-status" /></span>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-list">
          {data.messages.map((m) => <Message key={m.id} m={m} />)}<div ref={bottom} />
        </div>
        <ReplyBox conv={c} onSent={reload} />
      </div>
      <SidePanel data={data} agents={agents} reload={reload} />
    </div>
  );
}

export default function SupportInbox() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [f, setF] = useState({ view: "all", status: "", q: "", page: 1 });
  const [list, setList] = useState({ items: [], total: 0, page_size: 20 });
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/staff/chat/conversations", { params: f }), api.get("/staff/chat/stats")]);
      setList(l.data); setStats(s.data); setError(null);
    } catch (e) { setError(errMsg(e)); throw e; }
  }, [f]);
  usePoll(load, [JSON.stringify(f)]);
  useEffect(() => { api.get("/staff/chat/agents").then((r) => setAgents(r.data)).catch(() => {}); }, []);
  const pages = Math.max(1, Math.ceil(list.total / list.page_size));
  const open = (cid) => nav(`/app/support/${cid}`);
  return (
    <div>
      <PageHeader eyebrow="Customer care" title="Support inbox"
        description={can("support.inbox") ? "Chat with consumers who write from the CASURECO II mobile app." : "Conversations routed to your team from the CASURECO II mobile app."} />
      <Counters stats={stats} />
      <div className="grid lg:grid-cols-[minmax(300px,380px)_1fr] gap-4 min-w-0">
        <div className={`bg-white border border-slate-200 rounded-lg min-w-0 flex flex-col ${id ? "hidden lg:flex" : ""}`}>
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="flex flex-wrap gap-1" role="tablist">{VIEWS.map(([v, l]) => (
              <button key={v} role="tab" onClick={() => setF({ ...f, view: v, page: 1 })} data-testid={`inbox-tab-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${f.view === v ? "bg-blue-700 text-white" : "bg-slate-100 hover:bg-slate-200"}`}>{l}</button>))}</div>
            <div className="flex gap-2">
              <Input placeholder="Search subject, name, reference, account, email" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} data-testid="inbox-search" />
              <NativeSelect value={f.status} onChange={(s) => setF({ ...f, status: s, page: 1 })} placeholder="Any status" testId="inbox-status-filter" className="w-36"
                options={STATUSES.map((s) => ({ value: s, label: s }))} />
            </div>
          </div>
          {error && <p className="text-xs text-rose-700 p-3" data-testid="inbox-error">Could not refresh: {error}. Retrying…</p>}
          <div className="flex-1 overflow-y-auto max-h-[60vh]" data-testid="conversation-list">
            {list.items.length === 0 ? <div className="p-4"><Empty>No conversations match.</Empty></div>
              : list.items.map((c) => <ConversationRow key={c.id} c={c} active={c.id === id} onOpen={open} />)}
          </div>
          <div className="flex items-center justify-between p-3 border-t border-slate-100 text-xs">
            <span data-testid="inbox-total">{list.total} conversation{list.total === 1 ? "" : "s"}</span>
            <div className="flex gap-2 items-center">
              <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="inbox-prev">Prev</Button>
              <span>{f.page} / {pages}</span>
              <Button size="sm" variant="outline" disabled={f.page >= pages} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="inbox-next">Next</Button>
            </div>
          </div>
        </div>
        <div className={`min-w-0 ${id ? "" : "hidden lg:block"}`}>
          {id ? <ConversationView key={id} id={id} agents={agents} onBack={() => nav("/app/support")} onChanged={() => {
            setList((l) => ({ ...l, items: l.items.map((c) => (c.id === id ? { ...c, staff_unread: 0 } : c)) })); load().catch(() => {}); }} />
            : <Panel><Empty>Select a conversation to read and reply.</Empty></Panel>}
        </div>
      </div>
    </div>
  );
}
